/**
 * Adapter that reshapes a bid/design *package* log row into the
 * "material-shaped" row `FeatureBulkEditDurationsNewModel` / `PanelItemNewModel`
 * consume. Mirrors `mapPackageRowToWorkflowItem` in
 * `components/bulk-edit-status/derive-next-milestone`, which does the same job
 * for the Bulk Edit Status modal.
 *
 * Package rows (`bid_package_list_func`) carry two FLAT JSON arrays —
 * `milestones` and `offsets` — joined on
 * `offsets[].from_milestone_instance_id === milestones[].id`. That is the same
 * join the package grid's Duration columns use (see `getCurrentDuration` in
 * `entity-app/shared-components/entity-milestones-duration-columns`).
 *
 * Material rows instead carry a nested `date_block.milestones[]` where the
 * offset STARTING AT a milestone is pre-flattened onto it as `offset_id` /
 * `offset_name` / `offset_duration`.
 *
 * `date_block.milestones[].offset_id` (material) and `offsets[].id` (package)
 * are both the *feature workflow instance offset* id that
 * APPLY_WORKFLOW_MUTATION expects in `offsets[].id` — the two sibling handlers
 * in `entity-app/hooks/feature-log-v2-inline-edit` prove it:
 * `handleDu2DurationEdit` (packages) sends `currentDuration.id` and
 * `handleOffsetDurationEdit` (materials) sends `currentDuration.offset_id`,
 * both into the same mutation field.
 */
export function mapPackageRowToDurationRow(row: any): any {
  const offsets: any[] = row?.offsets ?? [];

  // Copy before sorting: `row.milestones` is the live ag-grid row-data array,
  // and mutating it would reorder the grid's Milestone / Duration columns.
  //
  // Sorting is required, not cosmetic: PanelItemNewModel's
  // `incomingDateBlockData` compares `feature.date_block[index].value`
  // POSITIONALLY across the features grouped under one template, and only
  // sorts afterwards. Package `milestones` is raw JSON with no order
  // guarantee (the grid always looks milestones up by `sequence_no`).
  const milestones = [...(row?.milestones ?? [])]
    .sort((a: any, b: any) => a.sequence_no - b.sequence_no)
    .map((milestone: any) => {
      const offset = offsets.find(
        (o: any) => o.from_milestone_instance_id === milestone.id
      );
      return {
        id: milestone.id,
        name: milestone.name,
        sequence_no: milestone.sequence_no,
        status: milestone.status,
        // The terminal milestone has no outgoing offset -> null, so
        // PanelItemNewModel's `workflow_template_offset_id && ...` filter drops
        // it from the save payload, and `milestone.offsetName && ...` in
        // workflow-template-bulk-editor renders no input for it. Same as
        // materials.
        offset_id: offset?.id ?? null,
        offset_name: offset?.name ?? "",
        offset_duration: offset?.duration ?? 0
      };
    });

  return {
    ...row,
    // NOTE: `workflow_status` is deliberately left as the plain string the
    // package row carries. Wrapping it as `{ code }` would satisfy
    // `transformToDateBlockData`'s (write-only) `.code` read, but would break
    // the `!== WF_COMPLETED` filter in FeatureBulkEditDurationsNewModel and
    // silently let closed packages into the editor.
    date_block: {
      id: row?.feature_workflow_instance_id,
      milestones
    }
  };
}

export default mapPackageRowToDurationRow;
